import { ModelIteratorBVH } from '../../../ModelIteratorBVH';
import { WebGLUploadTask } from './WebGLUploadTask';
import { UploadTaskBase } from './UploadTaskBase';
import { UniformUploadTask } from "./UniformUploadTask";

/** @import { BvhNode } from "../../BvhNode"; */

/**
 * Creates all tasks that are needed to upload the node to the GPU
 * for the WebGl renderer.
 *
 * @param {BvhNode} node - The ID of the BVH node
 * @param {Stage} stage - The stage of the task
 */
export function initializeOptimizedOnGpuTasksWebGL(node, stage) {
  const bvhNodeId = node.nodeId;
  const consolidation = node.model.getConsolidation(bvhNodeId);
  const fragList = node.model.getFragmentList();

  const bvhModelIterator = node.model.getIterator();

  if (!(bvhModelIterator instanceof ModelIteratorBVH)) {
    console.error("ModelIterator is not a BVH iterator");
    return;
  }

  if (node.transparent) {// transparent nodes cannot be consolidated because we need to sort individual fragments by depth
    return;
  }

  node.addTask(new WebGLUploadTask(node.outOfCoreTileManager, node), stage);

  // Add tasks for all remaining fragments
  const indices = consolidation.singleFragments;
  if (indices?.length > 0) {
    const geomIds = Uint32Array.from(new Set(indices.map((fragId) => fragList.getGeometryId(fragId))));
    const geomsPerTask = 100;
    for (let i = 0; i < geomIds.length; i += geomsPerTask) {
      node.addTask(new UploadTaskBase(node.outOfCoreTileManager, fragList, bvhNodeId, geomIds.subarray(i, i + geomsPerTask)), stage);
    }
  }
}

/**
 * Creates all tasks that are needed to upload the node to the GPU
 * for the WebGpu renderer.
 *
 * @param {BvhNode} node - The ID of the BVH node
 * @param {StageNames} stage - The stage of the task
 */
export function initializeOptimizedOnGpuTasksWebGpu(node, stage) {
  const bvhNodeId = node.nodeId;
  const fragList = node.model.getFragmentList();

  const bvhModelIterator = node.model.getIterator();

  if (!(bvhModelIterator instanceof ModelIteratorBVH)) {
    console.error("ModelIterator is not a BVH iterator");
    return;
  }

  const renderBatch = bvhModelIterator.getGeomScenes()[bvhNodeId];
  const indices = renderBatch.getIndices();

  let activeFragCount = 0;
  const geomIds = new Set();
  for (let i = renderBatch.start; i < renderBatch.lastItem; i++) {
    const fragId = indices[i];
    if (fragList.isFragmentActive(fragId)) {
      geomIds.add(fragList.getGeometryId(fragId));
      activeFragCount++;
    }
  }
  const geomIdsArray = Uint32Array.from(geomIds);
  const geomsPerTask = 100;
  for (let i = 0; i < geomIdsArray.length; i += geomsPerTask) {
    node.addTask(new UploadTaskBase(node.outOfCoreTileManager, fragList, bvhNodeId, geomIdsArray.subarray(i, i + geomsPerTask)), stage);
  }

  // Add a task to upload the batched Uniforms. Note that we rely here on the fact that tasks are executed strictly in
  // the given order. Reason is:
  //  - Uniform batches must be created in the same order as the fragments in the RenderBatch. So, we defer that until the order is fully settled.
  //  - Fragments in RenderBatch are sorted by GPU vertexBuffer page.
  //  - The vertex buffer page is not known until geometry is uploaded
  const uploadTask = new UniformUploadTask(node.outOfCoreTileManager, renderBatch, activeFragCount);
  node.addTask(uploadTask, stage);
}